/**
 * Data-driven route configuration.
 *
 * Every authenticated route is defined once in this array.
 * App.tsx iterates it to generate <Route> elements, eliminating
 * ~400 lines of repetitive JSX.
 */
import { type ComponentType, type LazyExoticComponent, lazy } from 'react';
// ── Eager imports for critical pages ─────────────────────
// These pages load immediately and shouldn't be code-split
// because they're accessed most frequently (Dashboard, LoanList, LoanDetail)
import { Dashboard } from '@/pages/Dashboard';
import { LoanDetail } from '@/pages/LoanDetail';
import { LoanList } from '@/pages/LoanList';

// ── Lazy-load helper ────────────────────────────────────

function page(
  importFn: () => Promise<{ [key: string]: ComponentType<any> }>,
  exportName: string,
): LazyExoticComponent<ComponentType<any>> {
  return lazy(() => importFn().then(m => ({ default: (m as any)[exportName] })));
}

// ── Route definition ────────────────────────────────────

export interface RouteConfig {
  path: string;
  component: ComponentType<any> | LazyExoticComponent<ComponentType<any>>;
  roles?: string[]; // omit = any authenticated user
}

// ── Authenticated routes (inside AppLayout) ─────────────

export const appRoutes: RouteConfig[] = [
  // Core — critical pages loaded eagerly
  { path: '/dashboard', component: Dashboard },
  { path: '/loans', component: LoanList },
  { path: '/loans/board', component: page(() => import('@/pages/LoanBoard'), 'LoanBoard') },
  { path: '/loans/compare', component: page(() => import('@/pages/LoanCompare'), 'LoanCompare') },
  { path: '/loans/:id', component: LoanDetail },
  { path: '/borrowers', component: page(() => import('@/pages/Borrowers'), 'Borrowers') },
  {
    path: '/borrowers/:id',
    component: page(() => import('@/pages/BorrowerDetail'), 'BorrowerDetail'),
  },
  { path: '/reports', component: page(() => import('@/pages/Reports'), 'Reports') },
  { path: '/search', component: page(() => import('@/pages/Search'), 'Search') },
  { path: '/calculator', component: page(() => import('@/pages/Calculator'), 'Calculator') },
  { path: '/collections', component: page(() => import('@/pages/Collections'), 'Collections') },
  { path: '/diary', component: page(() => import('@/pages/Diary'), 'Diary') },
  { path: '/portfolio', component: page(() => import('@/pages/Portfolio'), 'Portfolio') },
  { path: '/activity', component: page(() => import('@/pages/ActivityLog'), 'ActivityLog') },
  { path: '/pipeline', component: page(() => import('@/pages/Pipeline'), 'Pipeline') },
  {
    path: '/origination',
    component: page(() => import('@/pages/LoanOrigination'), 'LoanOrigination'),
  },
  { path: '/email', component: page(() => import('@/pages/EmailCenter'), 'EmailCenter') },
  {
    path: '/features',
    component: page(() => import('@/pages/FeatureRequests'), 'FeatureRequests'),
  },
  { path: '/bugs', component: page(() => import('@/pages/BugTracker'), 'BugTracker') },
  {
    path: '/chat-history',
    component: page(() => import('@/pages/MyChatHistory'), 'MyChatHistory'),
  },
  { path: '/billing', component: page(() => import('@/pages/BillingSettings'), 'BillingSettings') },
  { path: '/settings', component: page(() => import('@/pages/Settings'), 'Settings') },
  {
    path: '/settings/templates',
    component: page(() => import('@/pages/TemplateEditor'), 'TemplateEditor'),
  },

  // Role-guarded: ADMIN + MANAGER
  {
    path: '/import',
    component: page(() => import('@/pages/DataImport'), 'DataImport'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/admin/lenders',
    component: page(() => import('@/pages/Lenders'), 'Lenders'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/bulk-operations',
    component: page(() => import('@/pages/BulkOperations'), 'BulkOperations'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/compliance',
    component: page(() => import('@/pages/Compliance'), 'Compliance'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/automation',
    component: page(() => import('@/pages/Automation'), 'Automation'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/templates',
    component: page(() => import('@/pages/DocumentTemplates'), 'DocumentTemplates'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/trust-accounts',
    component: page(() => import('@/pages/TrustAccounts'), 'TrustAccounts'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/trust-accounts/:accountId/ledger',
    component: page(() => import('@/pages/TrustLedger'), 'TrustLedger'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/trust-accounts/:accountId/reconciliation/:statementId',
    component: page(() => import('@/pages/StatementReconciliation'), 'StatementReconciliation'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/trust-accounts/:accountId/manual-statement',
    component: page(() => import('@/pages/ManualStatementEntry'), 'ManualStatementEntry'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/settings/xero',
    component: page(() => import('@/pages/XeroSettings'), 'XeroSettings'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/settings/xero/setup',
    component: page(() => import('@/pages/XeroOnboarding'), 'XeroOnboarding'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/board-packs',
    component: page(() => import('@/pages/BoardPacks'), 'BoardPacks'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/alerts',
    component: page(() => import('@/pages/AlertsCenter'), 'AlertsCenter'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/regulatory-returns',
    component: page(() => import('@/pages/RegulatoryReturns'), 'RegulatoryReturns'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/data-exports',
    component: page(() => import('@/pages/DataExports'), 'DataExports'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/admin/chat-logs',
    component: page(() => import('@/pages/ChatLogs'), 'ChatLogs'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/admin/custom-dashboard',
    component: page(() => import('@/pages/CustomDashboard'), 'CustomDashboard'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/admin/qa-tracker',
    component: page(() => import('@/pages/QaTracker'), 'QaTracker'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/admin/bulk-comms',
    component: page(() => import('@/pages/BulkCommunications'), 'BulkCommunications'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/admin/doc-templates',
    component: page(() => import('@/pages/DocTemplateBuilder'), 'DocTemplateBuilder'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/admin/workflow-rules',
    component: page(() => import('@/pages/WorkflowRules'), 'WorkflowRules'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/admin/field-audit',
    component: page(() => import('@/pages/FieldAuditTrail'), 'FieldAuditTrail'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/admin/borrower-portal',
    component: page(() => import('@/pages/BorrowerPortal'), 'BorrowerPortal'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/admin/sms-center',
    component: page(() => import('@/pages/SmsCenter'), 'SmsCenter'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/admin/kpi-dashboard',
    component: page(() => import('@/pages/KpiDashboard'), 'KpiDashboard'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/treasury',
    component: page(() => import('@/pages/Treasury'), 'Treasury'),
    roles: ['ADMIN', 'MANAGER'],
  },
  {
    path: '/treasury/funding-lines',
    component: page(() => import('@/pages/FundingLines'), 'FundingLines'),
    roles: ['ADMIN', 'MANAGER'],
  },

  // Role-guarded: ADMIN + MANAGER + OFFICER
  {
    path: '/report-builder',
    component: page(() => import('@/pages/ReportBuilder'), 'ReportBuilder'),
    roles: ['ADMIN', 'MANAGER', 'OFFICER'],
  },
  {
    path: '/benchmarks',
    component: page(() => import('@/pages/Benchmarks'), 'Benchmarks'),
    roles: ['ADMIN', 'MANAGER', 'OFFICER'],
  },
  {
    path: '/analytics',
    component: page(() => import('@/pages/Analytics'), 'Analytics'),
    roles: ['ADMIN', 'MANAGER', 'OFFICER'],
  },
  {
    path: '/risk',
    component: page(() => import('@/pages/RiskDashboard'), 'RiskDashboard'),
    roles: ['ADMIN', 'MANAGER', 'OFFICER'],
  },
  {
    path: '/admin/report-engine',
    component: page(() => import('@/pages/ReportEngine'), 'ReportEngine'),
    roles: ['ADMIN', 'MANAGER', 'OFFICER'],
  },
  {
    path: '/admin/loan-terms',
    component: page(() => import('@/pages/LoanTerms'), 'LoanTerms'),
    roles: ['ADMIN', 'MANAGER', 'OFFICER'],
  },
  {
    path: '/admin/e-signature',
    component: page(() => import('@/pages/ESignature'), 'ESignature'),
    roles: ['ADMIN', 'MANAGER', 'OFFICER'],
  },

  // Role-guarded: ADMIN + MANAGER + COLLECTOR
  {
    path: '/collections/workflow',
    component: page(() => import('@/pages/CollectionsWorkflow'), 'CollectionsWorkflow'),
    roles: ['ADMIN', 'MANAGER', 'COLLECTOR'],
  },

  // Role-guarded: ADMIN only
  {
    path: '/admin/users',
    component: page(() => import('@/pages/UserManagement'), 'UserManagement'),
    roles: ['ADMIN'],
  },
  {
    path: '/admin/users/:id',
    component: page(() => import('@/pages/UserDetail'), 'UserDetail'),
    roles: ['ADMIN'],
  },
  {
    path: '/admin/audit',
    component: page(() => import('@/pages/AuditLog'), 'AuditLog'),
    roles: ['ADMIN'],
  },
  {
    path: '/admin/webhooks',
    component: page(() => import('@/pages/Webhooks'), 'Webhooks'),
    roles: ['ADMIN'],
  },
  {
    path: '/admin/authority',
    component: page(() => import('@/pages/DelegatedAuthority'), 'DelegatedAuthority'),
    roles: ['ADMIN'],
  },
  {
    path: '/admin/data-migration',
    component: page(() => import('@/pages/DataMigration'), 'DataMigration'),
    roles: ['ADMIN'],
  },
  {
    path: '/admin/api-portal',
    component: page(() => import('@/pages/ApiPortal'), 'ApiPortal'),
    roles: ['ADMIN'],
  },
  {
    path: '/admin/architecture',
    component: page(() => import('@/pages/SystemArchitecture'), 'SystemArchitecture'),
    roles: ['ADMIN'],
  },
];

// ── Super Admin routes (inside SuperAdminLayout) ────────

export const superAdminRoutes: RouteConfig[] = [
  {
    path: 'dashboard',
    component: page(() => import('@/pages/super-admin/SuperAdminDashboard'), 'SuperAdminDashboard'),
  },
  {
    path: 'features',
    component: page(() => import('@/pages/super-admin/FeatureFlags'), 'FeatureFlags'),
  },
  {
    path: 'licenses',
    component: page(() => import('@/pages/super-admin/LicenseDashboard'), 'LicenseDashboard'),
  },
  {
    path: 'billing',
    component: page(() => import('@/pages/super-admin/BillingManagement'), 'BillingManagement'),
  },
  {
    path: 'announcements',
    component: page(() => import('@/pages/super-admin/Announcements'), 'Announcements'),
  },
  {
    path: 'support',
    component: page(() => import('@/pages/super-admin/SupportTickets'), 'SupportTickets'),
  },
  {
    path: 'security',
    component: page(() => import('@/pages/super-admin/SecurityDashboard'), 'SecurityDashboard'),
  },
  {
    path: 'perf-monitor',
    component: page(
      () => import('@/pages/super-admin/SecurityPerformanceDashboard'),
      'SecurityPerformanceDashboard',
    ),
  },
  {
    path: 'alerts',
    component: page(() => import('@/pages/super-admin/AlertsIncidents'), 'AlertsIncidents'),
  },
  {
    path: 'performance',
    component: page(
      () => import('@/pages/super-admin/PerformanceDashboard'),
      'PerformanceDashboard',
    ),
  },
  {
    path: 'improvement',
    component: page(() => import('@/pages/super-admin/ImprovementHub'), 'ImprovementHub'),
  },
  { path: 'qa', component: page(() => import('@/pages/super-admin/QaCenter'), 'QaCenter') },
  {
    path: 'versions',
    component: page(() => import('@/pages/super-admin/VersionTracking'), 'VersionTracking'),
  },
  {
    path: 'developer',
    component: page(() => import('@/pages/super-admin/DeveloperPortal'), 'DeveloperPortal'),
  },
  {
    path: 'risk',
    component: page(() => import('@/pages/super-admin/RiskRegister'), 'RiskRegister'),
  },
];

// ── Lazy components used directly (login, layout) ───────

export const SuperAdminLogin = page(
  () => import('@/pages/super-admin/SuperAdminLogin'),
  'SuperAdminLogin',
);
export const SuperAdminLayout = page(
  () => import('@/pages/super-admin/SuperAdminLayout'),
  'SuperAdminLayout',
);
